import { take } from '../vfx-budget';
import * as BIG from './geo-big';
import { GC, band, core, fan, shard } from './geo-kit';
/* ============================================================
   風格 A：硬邊幾何衝擊 ← **使用者 2026-08-05 選定**
   ============================================================

   ## 一句話

   特效跟場景講同一種話：**不透明的多邊形與三角碎片**，零漸層。

   ## 為什麼是這個

   這個遊戲的每一樣東西 —— 地形、建築、機甲、敵人 —— 都是硬邊多面體。
   而第三版的特效是柔邊漸層的火與煙。兩種語言疊在一起，畫面就髒了，
   而且**不管怎麼調數值都救不回來**，因為問題不在數值在筆畫。

   ## 三條規則

   1. **一律一般混色、不透明度接近 1、幾乎不漸淡。**
      加法混色是「光」，一般混色是「物」。物會遮住背後的東西 ——
      那就是有實體的全部差別，也是不會被泛光糊掉的原因。
      漸淡是煙的行為；硬邊的東西只會消失，不會愈來愈透明。
   2. **形狀本身就是內容。** 沒有柔邊可以躲，所以每一片都得畫對：
      核心是多邊形（機械），碎片是不對稱三角形（被打下來的一塊），
      衝擊波是有厚度的多邊形帶（不是一圈光），地面留的是暗色焦痕。
   3. **色階只有三段，沒有中間色：白 → 冷白 → 暗。**
      無彩的白光是定案。沒有色相可用，所以**辨識度全部由明度階與形狀
      承擔** —— 白核心、冷白碎片、深色焦痕。

   ## 記憶點

   **碎片扇**。命中的瞬間從裝甲上剝下五到八片實心三角形，往回噴、
   吃重力、自轉。它們會擋住敵人 —— 那一瞬間的遮擋比任何亮度都像
   「打進去了」。

   ## 檔案分工

   - `geo-kit.ts`  五種基本筆畫（core / band / gband / shard / scorch）與調色
   - 本檔          主武器（槍口、命中）
   - `geo-big.ts`  副武器、大招、掛載、爆炸、焦痕 */

export var GEO: any = {
  id: 'geo',
  name: 'A · 硬邊幾何衝擊（已選定）',
  tag: '不透明多邊形＋三角碎片扇，白光三階，與場景同語言',
  c: GC,
  /* 強度還原成場景原本調好的 1.15（被上一版為了壓魔法感砍到 0.62），
     **但門檻要往上**。

     第一次實拍就踩到：門檻 0.66 時，不透明度 1 的白色多邊形亮度遠超門檻，
     整片被泛光糊成一顆球 —— 硬邊風格最怕的就是這個，形狀沒了就什麼都沒了。
     往上抬到 0.84：場景的火光與窗燈（自體發光、亮度更高）照樣過得去，
     而武器的白色平塗剛好留在門檻下，邊界保得住。

     ⚠️ 這是「調高」不是「調低」。`weapon-fx.ts` 檔頭那條「門檻一動特效會
     整批衝回來」講的是**調低**的方向，兩者不衝突。 */
  bloom: { strength: 1.15, radius: 0.6, threshold: 0.84 },
  punch: { shake: 0.022, critStop: 0.035, camPush: 0.20 },
  /* 曳光彈：比現行細一點、長一點，暗殼包白心。
     彈體在畫面上的時間比任何特效都長，它才是主武器質感的大宗，
     而現行版本是一根沒有輪廓的白色方棒。 */
  bullet: { c: 0xf2f8ff, w: 0.13, len: 2.6, ink: 0x151c26 },
  muzzle: muzzle,
  impact: impact,
  explode: BIG.explode,
  launch: BIG.launch,
  ultFire: BIG.ultFire,
  beamHit: BIG.beamHit,
  gadgetFire: BIG.gadgetFire,
  orbitalHit: BIG.orbitalHit,
  shockRing: BIG.shockRing,
  slashArc: BIG.slashArc,
  exhaust: BIG.exhaust
};

function muzzle(x: number, y: number, z: number, ang: number, power: number) {
  var big = power > 0.7;
  if (!take(big ? 9 : 5)) return;
  /* 尺寸的基準是**敵人的體型**（雜魚高約 1 公尺、`entities/families/*` 的 `h`），
     不是預覽台上放大過的靶機。第一版照預覽調到 2.5–3 公尺，進遊戲之後
     一發槍口就蓋掉半個機體 —— 這是「驗收要在真的遊戲裡」那條的第 N 次覆誦。 */
  var s = 1.0 + power * 2.2;
  /* ① 六邊形核心。**先大後小**（s0 > s1）：硬邊的東西擴散出去會讀成
     「一個環在跑」，收回來才讀成「打出去的那一下」。 */
  core(6, x, y, z, s * 1.05, s * 0.45, 0.055, GC.hot);
  /* ② 沿射向擴出去的厚帶。

     `op: 1` + 不漸淡是實拍後改的：第一版用 0.85 + 漸淡，在淺色柏油上
     讀成**一圈灰色的 UI 標記**而不是衝擊 —— 一般混色的白色只要不透明度
     掉下來就會變灰，而灰色的環在這個場景裡是資訊的顏色。
     尺寸也收了一輪（1.9 → 1.35 倍）：太大的環會蓋過機體本身。 */
  band(x + Math.sin(ang) * s * 0.35, y, z + Math.cos(ang) * s * 0.35,
    0.30, s * 0.45, s * 1.35, 0.075, GC.acc);
  /* ③ 前噴的碎片（火藥推出去的東西）與 ④ 反衝的碎片（後座）。 */
  shard(x, y, z, ang, 0.45, GC.hot, 15, 0.62);
  shard(x, y, z, ang, 0.45, GC.chip, 12, 0.5);
  shard(x, y, z, ang + Math.PI, 0.6, GC.chip, 9, 0.42);
  if (!big) return;
  fan(x, y, z, ang + Math.PI * 0.85, 0.8, 4, 18, 0.8);
}

function impact(x: number, y: number, z: number, ang: number, crit: boolean) {
  if (!take(crit ? 13 : 8)) return;
  var k = crit ? 1.7 : 1;
  /* ① 五邊形白核：命中點要有一個「戳進去」的實心形狀，不是一團光。
     尺寸只比命中點大一點點（0.8 公尺，雜魚高約 1 公尺）。
     第一版給 2.6，實拍是一塊**比敵人還大的白色五邊形**，
     把「打到誰」整個蓋掉 —— 命中特效不該遮住命中的對象。 */
  core(5, x, y, z, 0.8 * k, 0.28 * k, 0.065, GC.hot);
  // ② 衝擊帶：擴散但**不透明**，讀成被切出來的環而不是光暈
  band(x, y, z, 0.26, 0.7 * k, 2.6 * k, 0.12, GC.acc);
  /* ③ **碎片扇 —— 這個風格的記憶點。**
     往命中的反方向噴（子彈把它們帶出來），吃重力、自轉、會遮住敵人。 */
  fan(x, y, z, ang + Math.PI, crit ? 1.15 : 0.8, crit ? 8 : 5,
    crit ? 16 : 11, crit ? 0.95 : 0.68);
  if (!crit) return;
  // ④ 暴擊多一層更大、更慢的帶：兩層不同速度讀成「衝擊有厚度」
  band(x, y, z, 0.14, 1.0, 3.6, 0.17, GC.hot, 0.85);
}
